import React from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { LoadingIcon } from "../../../common";
import { Modal } from "../../../common/Modal";
import { LockIcon } from "../../../../assets/ui/icons";
import { Aed } from "../../../common/Money";
import CheckoutForm from "../Plans/checkoutForm";
import { BillingAddressForm } from "../../../common/u1/BillingAddressForm";
import { useBillingAddressForm } from "../../../common/u1/useBillingAddressForm";
import { BillingAddressBlock } from "./BillingAddressBlock";
import { BillingAddressPicker } from "./BillingAddressPicker";
import { PaymentSuccessDialog } from "./PaymentSuccessDialog";

/* The left column: who the invoice goes to, then the card form.
 *
 * CheckoutForm is the live Stripe component, imported unchanged — it holds
 * confirmPayment/confirmSetup, the return_url and the TIP-564 cleanup on a
 * declined card. §5 of the rulebook: payments are touched only in their
 * scheduled work, so it is reused, not rewritten. Its submit button is dressed
 * in u1 clothes by a scoped rule in tokens.css, which changes no payment code.
 *
 * The add and edit address forms are now one u1 form (BillingAddressForm) with
 * the validation extracted to a single module — they used to be two 645-line
 * near-duplicates with a private copy of the rules each. See F-086.
 */
export const PaymentPanel = ({
  checkout,
  planDetails,
  summary,
  copy,
  isReportRequest,
  onBack,
  className = "",
}) => {
  /* Both dialogs run the same form; only the record they start from differs.
     A saved address re-reads the list and lands back on the picker, which is
     what the two forms it replaces did between them. */
  const afterSave = () => {
    checkout.setHasAddressDataChanged((v) => !v);
    checkout.openAddressList();
  };
  const addForm = useBillingAddressForm({ onSaved: afterSave });
  const editForm = useBillingAddressForm({
    address: checkout.existingBilling,
    onSaved: afterSave,
  });

  /* Created per render, as the live form does — loadStripe caches on the key,
     so this is one script load however many times it is called. */
  const stripePromise = loadStripe(process.env.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY);
  const options = { clientSecret: checkout.clientSecret, appearance: { theme: "stripe" } };

  return (
    <div className={className}>
      <section
        className={cn(
          "rounded-[14px] border border-[var(--line)] bg-white p-4 sm:p-6"
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
              Payment details
            </h2>
            <p className="m-0 mt-1.5 max-w-[46ch] text-[12.5px] leading-[1.55] text-[var(--muted)] font-sans">
              {copy}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack}>
            Back
          </Button>
        </div>

        <BillingAddressBlock
          className="mt-4"
          address={checkout.hasBillingAddress ? checkout.primary : null}
          userName={checkout.userInfo?.name}
          onChange={checkout.openAddressList}
          onAdd={checkout.openAddAddress}
        />

        {checkout.hasBillingAddress && (
          <div className="mt-5">
            {checkout.preparing ? (
              <div
                role="status"
                aria-live="polite"
                className="flex flex-col items-center gap-2 py-6"
              >
                <LoadingIcon />
                <p className="m-0 text-[12.5px] text-[var(--muted)] font-sans">
                  Preparing your secure payment…
                </p>
              </div>
            ) : (
              checkout.clientSecret &&
              checkout.clientSecret !== "PAID" && (
                <>
                  <div className="flex items-center gap-2 border-t border-[var(--line)] pt-4">
                    <span
                      aria-hidden="true"
                      className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--field-inset)] text-[var(--muted)]"
                    >
                      <LockIcon size={13} />
                    </span>
                    <p className="m-0 text-[12px] text-[var(--muted)] font-sans">
                      Card details are entered on Stripe. TREVEX never sees them.
                    </p>
                  </div>

                  <div className="mt-4">
                    <Elements options={options} stripe={stripePromise}>
                      <CheckoutForm
                        name={checkout.userInfo?.name}
                        ShippingAddress={checkout.shippingAddress}
                        clientSecret={checkout.clientSecret}
                        pendingSetup={checkout.pendingSetup}
                        title="Web Profile"
                        plan={planDetails}
                        payLabel={
                          <>
                            Pay&nbsp;
                            <Aed value={summary?.total ?? 0} />
                          </>
                        }
                        isReportRequest={isReportRequest}
                      />
                    </Elements>
                  </div>
                </>
              )
            )}
          </div>
        )}
      </section>

      {checkout.dialog === "list" && (
        <Modal modalHandle={checkout.closeDialog} noClose bare>
          <BillingAddressPicker
            addresses={checkout.addresses}
            userName={checkout.userInfo?.name}
            refetch={checkout.refetchExistingBilling}
            hasAddressDataChanged={checkout.hasAddressDataChanged}
            setHasAddressDataChanged={checkout.setHasAddressDataChanged}
            onAdd={checkout.openAddAddress}
            onEdit={checkout.openEditAddress}
            onDelete={checkout.deleteAddress}
            onPicked={checkout.closeDialog}
          />
        </Modal>
      )}

      {checkout.dialog === "add" && (
        <Modal modalHandle={checkout.closeDialog} noClose bare>
          <BillingAddressForm form={addForm} onCancel={checkout.closeDialog} />
        </Modal>
      )}

      {checkout.dialog === "edit" && (
        <Modal modalHandle={checkout.closeDialog} noClose bare>
          <BillingAddressForm form={editForm} onCancel={checkout.closeDialog} />
        </Modal>
      )}

      {checkout.paidDialogOpen && (
        <Modal modalHandle={checkout.finishPaidDialog} noClose bare>
          <PaymentSuccessDialog onContinue={checkout.finishPaidDialog} />
        </Modal>
      )}
    </div>
  );
};

export default PaymentPanel;
